import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowUpCircle, CameraOff, Cpu, Eye, History, Loader2, MonitorPlay,
  Play, Power, RefreshCw, RotateCcw, RotateCw, Square, Terminal, Undo2, X
} from 'lucide-react';
import { deviceCommandsApi, CommandStatus } from '../services/deviceCommandsApi';
import { devicesApi } from '../services/devicesApi';
import { softwareApi, CurrentBuild } from '../services/softwareApi';
import { useAuth } from '../contexts/AuthContext';
import { Device, SystemStatusResponse } from '../types/devices';
import { getTimeAgo, parseUTCDate } from '../utils/dateUtils';
import { StatusPill } from './StatusIndicator';
import DeviceTerminal from './DeviceTerminal';

interface DeviceControlPanelProps {
  device: Device;
  onClose?: () => void;
}

/**
 * Commands that install software and are allowed to take as long as they take.
 *
 * apt and pip do not fit in a minute, and cutting either short does not stop the
 * work - it only stops us watching it, while the package transaction carries on
 * holding the dpkg lock. So the device gives these no limit and this waits.
 */
const LONG_RUNNING_COMMANDS = new Set(['update-device', 'rollback-device']);

// Captures return a picture, and their text is only the picture's metadata.
const CAPTURE_COMMANDS = new Set(['whats-on-screen', 'whats-on-camera']);

// Commands that take the Pi down, and so may never report that they finished.
const POWER_COMMANDS = new Set(['reboot-system', 'shutdown-system']);

const COMMAND_NAMES: Record<string, string> = {
  'start-player': 'Start',
  'stop-player': 'Stop',
  'restart-player': 'Restart',
  'update-device': 'Update',
  'rollback-device': 'Roll Back',
  'reboot-system': 'Reboot',
  'shutdown-system': 'Shut Down',
  'whats-on-screen': "What's on Screen",
  'whats-on-camera': "What's on Camera",
  // No longer offered, but still in the history of devices that ran them.
  'get-system-info': 'System info',
  'clear-log-files': 'Clear log files',
  'clear-cache': 'Clear cache',
  'test-camera': 'Test camera',
};

// DescribeIssuer on the API appends this to a super admin working inside a
// customer's company.
const SUPPORT_SUFFIX = ' (Pinage support)';

type CaptureKind = 'screen' | 'camera';

interface Capture {
  image?: string;
  capturedAt?: Date;
  resolution?: string;
  error?: string;
}

const toDate = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  const date = typeof value === 'string' ? parseUTCDate(value) : value;
  return isNaN(date.getTime()) || date.getFullYear() <= 1 ? null : date;
};

/** "Today 14:02", "Yesterday 18:40", "27 Sep 18:40". */
const formatWhen = (value?: string | Date | null): string => {
  const date = toDate(value);
  if (!date) return '';

  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return `Today ${time}`;
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
  return `${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${time}`;
};

/** "6 days", "3 hours", "12 minutes". */
const formatUptime = (seconds?: number | null): string | null => {
  if (seconds === undefined || seconds === null) return null;
  const days = Math.floor(seconds / 86400);
  if (days >= 1) return days === 1 ? '1 day' : `${days} days`;
  const hours = Math.floor(seconds / 3600);
  if (hours >= 1) return hours === 1 ? '1 hour' : `${hours} hours`;
  const minutes = Math.max(1, Math.floor(seconds / 60));
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
};

/** How long a finished command took, when it was long enough to be worth saying. */
const describeDuration = (command: CommandStatus): string | null => {
  const started = toDate(command.startedAt);
  const finished = toDate(command.completedAt);
  if (!started || !finished) return null;
  const minutes = Math.round((finished.getTime() - started.getTime()) / 60000);
  return minutes >= 1 ? `took ${minutes} min` : null;
};

const describeOutcome = (command: CommandStatus): { label: string; className: string } => {
  switch (command.status) {
    case 'pending':
      return { label: 'Waiting for the Pi', className: 'bg-amber-100 text-amber-800' };
    case 'running':
      return { label: 'Running', className: 'bg-blue-100 text-blue-800' };
    case 'cancelled':
      return { label: 'Cancelled', className: 'bg-gray-100 text-gray-700' };
    case 'failed':
      return { label: 'Failed', className: 'bg-red-100 text-red-800' };
    default:
      // The device reports a script that exited non-zero as completed, with
      // the exit code beside it.
      return command.exitCode !== undefined && command.exitCode !== null && command.exitCode !== 0
        ? { label: 'Failed', className: 'bg-red-100 text-red-800' }
        : { label: 'Completed', className: 'bg-green-100 text-green-800' };
  }
};

/** When and at what size, from the JSON a capture reports alongside its picture. */
const readCaptureMeta = (result?: string): { capturedAt?: Date; resolution?: string } => {
  try {
    const meta = JSON.parse(result || '{}');
    return {
      capturedAt: toDate(meta.captured_at) ?? undefined,
      // Only a real size is worth showing. The screen reports 'screen' or
      // 'mpv-output' here, which says how it was taken rather than how big.
      resolution: typeof meta.resolution === 'string' && /^\d+x\d+$/.test(meta.resolution)
        ? meta.resolution.replace('x', '×')
        : undefined,
    };
  } catch {
    return {};
  }
};

const describeError = (err: unknown): string => {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  return err instanceof Error ? err.message : 'Command failed';
};

// ----------------------------------------------------------------------------
// Pieces
// ----------------------------------------------------------------------------

interface ActionButtonProps {
  icon: React.ElementType;
  label: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  // Amber for the one consequence that does not put itself right.
  caution?: boolean;
}

/**
 * Every action says what it will do to the screen, under its name. These are
 * pressed rarely and usually under pressure, which is when nobody remembers
 * that Restart blanks the screen for a moment and Stop blanks it until someone
 * comes back.
 */
const ActionButton: React.FC<ActionButtonProps> = ({
  icon: Icon, label, hint, onClick, disabled, primary, caution
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`flex min-h-[52px] w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
      primary
        ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
        : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
    }`}
  >
    <Icon className={`h-[18px] w-[18px] flex-shrink-0 ${primary ? 'text-white' : 'text-gray-500'}`} />
    <span className="flex flex-col">
      <span className="text-sm font-medium">{label}</span>
      <span className={`text-xs ${primary ? 'text-blue-100' : caution ? 'text-amber-700' : 'text-gray-500'}`}>
        {hint}
      </span>
    </span>
  </button>
);

const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900">{value}</span>
  </div>
);

const Reasons: React.FC<{ reasons: string[] }> = ({ reasons }) =>
  reasons.length === 0 ? null : (
    <ul className="space-y-0.5 text-xs font-medium text-amber-700">
      {reasons.map((reason, i) => (
        <li key={i} className="flex items-start">
          <span className="mr-1 leading-none">•</span>
          <span>{reason}</span>
        </li>
      ))}
    </ul>
  );

const EmptyPicture: React.FC<{ aspect: string; children: React.ReactNode }> = ({ aspect, children }) => (
  <div className={`flex ${aspect} flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center`}>
    {children}
  </div>
);

interface CapturePaneProps {
  title: string;
  kind: CaptureKind;
  shot?: Capture;
  capturing: boolean;
  disabled: boolean;
  greyed?: boolean;
  empty: React.ReactNode;
  onCapture: () => void;
}

/**
 * One picture and the button that takes it. The picture stays until the next
 * one replaces it: the old result box cleared everything but the screen after
 * five seconds, camera frames included.
 */
const CapturePane: React.FC<CapturePaneProps> = ({
  title, kind, shot, capturing, disabled, greyed, empty, onCapture
}) => {
  const aspect = kind === 'screen' ? 'aspect-video' : 'aspect-[4/3]';
  // Read from the data, not assumed from the kind: a screen capture is a JPEG
  // from mpv and a PNG from the console, and a camera frame is a JPEG.
  const mime = shot?.image?.startsWith('iVBOR') ? 'image/png' : 'image/jpeg';

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm font-medium ${greyed ? 'text-gray-400' : 'text-gray-800'}`}>{title}</span>
        <button
          type="button"
          onClick={onCapture}
          disabled={disabled}
          className="flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-[13px] font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {capturing
            ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-500" />
            : <RefreshCw className="h-3.5 w-3.5 text-gray-500" />}
          Capture
        </button>
      </div>

      {shot?.image && !greyed ? (
        <>
          <img
            src={`data:${mime};base64,${shot.image}`}
            alt={title}
            className={`w-full rounded-md bg-gray-900 object-contain ${aspect}`}
          />
          <span className="text-xs text-gray-500">
            Captured {formatWhen(shot.capturedAt)}{shot.resolution && ` · ${shot.resolution}`}
          </span>
        </>
      ) : (
        <EmptyPicture aspect={aspect}>
          {capturing ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="text-sm text-gray-600">Asking the Pi...</span>
            </>
          ) : empty}
        </EmptyPicture>
      )}

      {shot?.error && !greyed && <span className="text-xs text-red-700">{shot.error}</span>}
    </div>
  );
};

// ----------------------------------------------------------------------------
// The panel
// ----------------------------------------------------------------------------

/**
 * One device: what it is showing and seeing, the Pi and Pinage each with the
 * actions that belong to them, and what has recently been sent to it.
 *
 * The two cards are the Devices table's two status columns - Pi Health and
 * Pinage - with their buttons underneath, because health.py and system.py
 * answer different questions and each set of buttons acts on one of them. A
 * verdict of "Not running" sits directly above the Start that fixes it.
 *
 * Customers get the same panel less Terminal, Update and Roll Back. The Pis
 * belong to the hosting company and the content belongs to the customers, so
 * moving a device between releases, and a shell on it, stay with Pinage staff.
 */
export const DeviceControlPanel: React.FC<DeviceControlPanelProps> = ({ device, onClose }) => {
  const { user, actingAs } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const { deviceId, deviceName } = device;

  const [status, setStatus] = useState<SystemStatusResponse | null>(null);
  const [history, setHistory] = useState<CommandStatus[]>([]);
  const [inFlight, setInFlight] = useState<CommandStatus | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [capturing, setCapturing] = useState<CaptureKind | null>(null);
  const [shots, setShots] = useState<Partial<Record<CaptureKind, Capture>>>({});
  const [expanded, setExpanded] = useState<number | null>(null);
  const [showTerminal, setShowTerminal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentBuild, setCurrentBuild] = useState<CurrentBuild | null>(null);

  // Closing the panel stops the watching. The command itself carries on.
  //
  // Made inside the effect rather than as the ref's initial value: StrictMode
  // mounts, unmounts and remounts in development, and a controller made once
  // would be aborted by that first unmount and stay aborted for good.
  const watching = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    watching.current = controller;
    return () => controller.abort();
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      setStatus(await devicesApi.getDeviceStatus(deviceId));
    } catch {
      // The row the panel was opened from is still there to fall back on.
    }
  }, [deviceId]);

  const refreshHistory = useCallback(async () => {
    try {
      setHistory(await deviceCommandsApi.getCommandHistory(deviceId, 8));
    } catch {
      // Keep what is showing: a list that failed to refresh is less wrong
      // than an empty one.
    }
  }, [deviceId]);

  useEffect(() => {
    refreshStatus();
    refreshHistory();
  }, [refreshStatus, refreshHistory]);

  // Only the build lines beside Update need it, and only a super admin sees
  // Update, so nobody else's panel makes the request.
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    softwareApi.getCurrentBuild().then(result => {
      if (!cancelled) setCurrentBuild(result);
    });
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  // Anything still waiting or running when the panel opened - an update started
  // earlier, a Restart queued while the Pi was away - is watched until it
  // settles, so the list never shows a finished command as still going.
  const unsettled = history.some(c => c.status === 'pending' || c.status === 'running');
  useEffect(() => {
    if (!unsettled || inFlight) return;
    const timer = setInterval(refreshHistory, 10000);
    return () => clearInterval(timer);
  }, [unsettled, inFlight, refreshHistory]);

  // The row the panel was opened from, overlaid with what the API says now.
  const healthStatus = status?.healthStatus ?? device.healthStatus;
  const healthReasons = status?.healthReasons ?? device.healthReasons ?? [];
  const pinageStatus = status?.pinageStatus ?? device.pinageStatus;
  const pinageReasons = status?.pinageReasons ?? device.pinageReasons ?? [];
  // The camera is the Pi's, so it comes from health.py and is known with Pinage
  // stopped. Working is null until audience.py has tried it.
  const cameraDetected = status ? status.cameraDetected : device.cameraDetected;
  const cameraType = status?.cameraType ?? null;
  const cameraWorking = status?.cameraWorking ?? null;
  const metrics = status?.metrics ?? device.currentStatus;
  const lastHealthReport = status?.lastHealthReport ?? device.lastHeartbeat;
  const lastPinageReport = status?.lastPinageReport ?? device.lastPinageReport;
  const boardModel = status?.boardModel ?? device.boardModel;
  const pinageVersion = status?.pinageVersion ?? device.pinageVersion;
  const pinageBuild = status?.pinageBuild ?? device.pinageBuild;
  const stagedBuild = status?.stagedBuild ?? device.stagedBuild;

  const healthFresh = healthStatus === 'Online' || healthStatus === 'Warning';
  const pinageFresh = pinageStatus === 'Running' || pinageStatus === 'Warning';
  const pinageStopped = pinageStatus === 'Not running';
  const noCamera = cameraDetected === false;
  const cameraBroken = cameraDetected === true && cameraWorking === false;

  // ==========================================================================
  // NOTHING IS SENT TO A DEVICE THAT IS NOT REPORTING
  // ==========================================================================
  // A command sent to a silent device is not refused. It waits on the server
  // and runs whenever the Pi next connects, however long that takes - one
  // queued Stop ran twenty-five hours late and left a shop's screen dark. So
  // while neither report is arriving the buttons are off, and the one thing
  // left to do is cancel what is already waiting.
  const reachable = healthFresh || pinageFresh;
  const canSend = reachable && !isExecuting;

  const lastSeen = [toDate(lastHealthReport), toDate(lastPinageReport)]
    .filter((d): d is Date => d !== null)
    .sort((a, b) => b.getTime() - a.getTime())[0];

  const rows = inFlight && !history.some(c => c.id === inFlight.id)
    ? [inFlight, ...history]
    : history.map(c => (inFlight && c.id === inFlight.id ? inFlight : c));
  const hasWaiting = rows.some(c => c.status === 'pending');

  const companyName = actingAs?.companyName ?? user?.companyName;
  const locationName = device.location?.locationName;

  /**
   * "You" for your own, "Pinage support" for staff when a customer is looking,
   * and the address otherwise.
   */
  const describeIssuer = (issuedBy?: string): string => {
    if (!issuedBy) return 'Unknown';
    const fromSupport = issuedBy.endsWith(SUPPORT_SUFFIX);
    const email = fromSupport ? issuedBy.slice(0, -SUPPORT_SUFFIX.length) : issuedBy;
    if (user?.email && email.toLowerCase() === user.email.toLowerCase()) return 'You';
    if (fromSupport && !isSuperAdmin) return 'Pinage support';
    return issuedBy;
  };

  /**
   * The build lines in the Update confirmation.
   *
   * Four states are worth telling apart, and each is a different decision:
   * the device has never reported a build (installed before build ids), the
   * server has none recorded (a zip published before them), a build is already
   * downloaded but not installed, and - the one that saves a pointless fleet
   * update - the device is already on the build being offered.
   */
  const describeBuildChange = (): string => {
    const installed = pinageBuild
      ? `build ${pinageBuild}`
      : 'no build id reported (installed before build ids)';
    const versionPrefix = pinageVersion ? `${pinageVersion}, ` : '';
    const lines = [`Installed now: ${versionPrefix}${installed}`];

    if (!currentBuild?.available) {
      lines.push('Will install: the latest published build');
      return lines.join('\n');
    }

    lines.push(`Will install: ${currentBuild.build
      ? `build ${currentBuild.build}`
      : 'the latest published build (no build id recorded)'}`);

    if (stagedBuild && stagedBuild !== pinageBuild) {
      lines.push(`Already downloaded, not yet installed: build ${stagedBuild}`);
    }

    if (currentBuild.build && currentBuild.build === pinageBuild) {
      lines.push('', 'This device already has that build. Updating reinstalls it.');
    }

    return lines.join('\n');
  };

  const execute = async (command: string): Promise<CommandStatus | null> => {
    const signal = watching.current?.signal;
    let lastStatus: string = 'pending';
    setError(null);
    setIsExecuting(true);

    try {
      const issued = await deviceCommandsApi.issueCommand(deviceId, command);
      setInFlight({
        id: issued.commandId,
        companyId: 0,
        deviceId,
        command,
        status: 'pending',
        issuedBy: user?.email ?? '',
        issuedAt: issued.issuedAt,
      });

      // Installing software is given no time limit on the device, so wait half
      // an hour here and check less often. Every three seconds for thirty
      // minutes is 600 requests; every second would be 1,800 to learn the same
      // thing about a job that reports its own progress twice a minute anyway.
      // Its output is the record of what happened to the device, so it opens.
      const isInstall = LONG_RUNNING_COMMANDS.has(command);
      if (isInstall) setExpanded(issued.commandId);

      return await deviceCommandsApi.pollCommandCompletion(
        issued.commandId,
        isInstall ? 600 : 240,
        isInstall ? 3000 : 1000,
        progress => {
          lastStatus = progress.status;
          setInFlight(progress);
        },
        signal
      );
    } catch (err) {
      // A Pi on an older build reboots in the middle of the command and never
      // says it finished. Once it has picked the command up, that silence is
      // the command working, not failing.
      const wentDown = POWER_COMMANDS.has(command) && lastStatus === 'running';
      if (!signal?.aborted && !wentDown) setError(describeError(err));
      return null;
    } finally {
      if (!signal?.aborted) {
        await refreshHistory();
        setInFlight(null);
        setIsExecuting(false);
        refreshStatus();
      }
    }
  };

  const capture = async (kind: CaptureKind) => {
    setCapturing(kind);
    const result = await execute(kind === 'screen' ? 'whats-on-screen' : 'whats-on-camera');
    setCapturing(null);
    if (!result) return;

    setShots(previous => {
      if (result.status === 'completed' && result.resultImage) {
        const meta = readCaptureMeta(result.result);
        return {
          ...previous,
          [kind]: {
            image: result.resultImage,
            capturedAt: meta.capturedAt ?? toDate(result.completedAt) ?? new Date(),
            resolution: meta.resolution,
          },
        };
      }
      // Keep the last good picture and say why this one did not come.
      return {
        ...previous,
        [kind]: { ...previous[kind], error: result.result || 'No picture came back.' },
      };
    });
  };

  const cancel = async (commandId: number) => {
    setError(null);
    try {
      await deviceCommandsApi.cancelCommand(commandId);
    } catch (err) {
      setError(describeError(err));
    }
    refreshHistory();
  };

  // Stop is the one action that does not put itself right: the screen stays
  // dark until somebody comes back and presses Start. It used to be the one
  // with no confirmation.
  const stop = () => {
    if (window.confirm(
      `Stop Pinage on ${deviceName}?\n\n` +
      `The screen stays dark until someone presses Start, or the Pi restarts.`
    )) {
      execute('stop-player');
    }
  };

  // The other action that does not put itself right: nothing on the Pi can
  // turn it back on. It is for switching a Pi off properly before the power is
  // pulled, which is why the confirmation says who has to bring it back.
  const shutDown = () => {
    if (window.confirm(
      `Shut down ${deviceName}?\n\n` +
      `It stays off until someone at the site turns it back on, by unplugging ` +
      `the power and plugging it in again, or with the power button on a Pi 5.`
    )) {
      execute('shutdown-system');
    }
  };

  const reboot = () => {
    if (window.confirm(
      `Reboot ${deviceName}?\n\n` +
      `It goes offline for about a minute, and the screen is blank until it is back.`
    )) {
      execute('reboot-system');
    }
  };

  // Update means update.sh, setup.sh, deploy.sh and refresh.sh, in that order,
  // stopping at the first failure - the sequence that used to be done by hand
  // over Pi Connect.
  const update = () => {
    if (window.confirm(
      `Update ${deviceName}?\n\n` +
      `${describeBuildChange()}\n\n` +
      `This runs update, setup, deploy and refresh on the device. ` +
      `It can take several minutes and the screen will go blank ` +
      `while the services restart.`
    )) {
      execute('update-device');
    }
  };

  const rollBack = () => {
    if (window.confirm(
      `Roll ${deviceName} back?\n\n` +
      `This puts the device back on the release it was running before ` +
      `the last update and restarts the services, so the screen goes ` +
      `blank for a moment. Media, settings and this device's rotation ` +
      `are left alone.`
    )) {
      execute('rollback-device');
    }
  };

  const updateHint = (() => {
    const build = currentBuild?.available ? currentBuild.build : null;
    if (!build) return 'Installs the latest build · takes several minutes';
    return build === pinageBuild
      ? `Reinstalls build ${build} · takes several minutes`
      : `Installs build ${build} · takes several minutes`;
  })();

  const waitingForPi = (
    <>
      <span className="text-sm font-medium text-gray-700">Waiting for the Pi</span>
      <span className="max-w-xs text-[13px] leading-relaxed text-gray-500">
        Nothing can be captured until it reports again.
      </span>
    </>
  );

  const services = status?.services ?? {};

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold text-gray-900">{deviceName}</h2>
          <span className="text-[13px] text-gray-500">
            {[companyName, locationName ?? 'No location'].filter(Boolean).join(' · ')}
          </span>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-3 -mt-2.5 flex h-11 w-11 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {!reachable && (
        <div className="mx-6 mt-5 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
          <AlertTriangle className="mt-0.5 h-[18px] w-[18px] flex-shrink-0" />
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold">
              {lastSeen ? `Not heard from since ${formatWhen(lastSeen)}` : 'Not heard from yet'}
            </span>
            <span className="text-[13px] leading-relaxed">
              Anything sent now would wait on the server and run whenever the Pi next
              connects, which could be days from now. Commands are switched off until it
              reports again.{hasWaiting && ' One already waiting can be cancelled below.'}
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-5 px-6 pb-6 pt-5">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Camera and screen: together because they are the two ways of looking
            through the device - what it sees, and what it shows. The camera
            sits over the Pi card and the screen over the Pinage card, the side
            each belongs to: health.py reports the camera, and what is on the
            screen is Pinage's doing. */}
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-gray-600">
            <Eye className="h-[18px] w-[18px]" />
            <h3 className="text-[15px] font-semibold text-gray-900">Camera and screen</h3>
          </div>
          <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-[minmax(0,12fr)_minmax(0,16fr)]">
            <CapturePane
              title="What the camera sees"
              kind="camera"
              shot={shots.camera}
              capturing={capturing === 'camera'}
              disabled={!canSend || noCamera}
              greyed={noCamera}
              onCapture={() => capture('camera')}
              empty={noCamera ? (
                <>
                  <CameraOff className="mb-0.5 h-7 w-7 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">No camera detected</span>
                  {/* Ribbon cameras are found only when the Pi starts - the
                      firmware probes the connector at boot - so one fitted to a
                      running Pi stays invisible until it reboots. */}
                  <span className="max-w-xs text-[13px] leading-relaxed text-gray-500">
                    No audience figures until the Pi finds one. A USB camera is picked up on
                    its own; a ribbon camera only when the Pi starts, so reboot after fitting one.
                  </span>
                </>
              ) : !reachable ? waitingForPi : cameraBroken ? (
                <>
                  <AlertTriangle className="mb-0.5 h-7 w-7 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Camera found but not working</span>
                  <span className="max-w-xs text-[13px] leading-relaxed text-gray-500">
                    The Pi can see a camera, but no picture is coming from it.
                  </span>
                </>
              ) : pinageStopped ? (
                <>
                  <span className="text-sm font-medium text-gray-700">Not captured yet</span>
                  <span className="max-w-xs text-[13px] leading-relaxed text-gray-500">
                    With Pinage stopped this opens the camera directly, which only works
                    for a ribbon camera.
                  </span>
                </>
              ) : (
                <span className="text-[13px] text-gray-500">Press Capture to see what the camera sees.</span>
              )}
            />
            <CapturePane
              title="What's on screen"
              kind="screen"
              shot={shots.screen}
              capturing={capturing === 'screen'}
              disabled={!canSend}
              onCapture={() => capture('screen')}
              empty={reachable ? (
                <span className="text-[13px] text-gray-500">Press Capture to see what's on screen now.</span>
              ) : waitingForPi}
            />
          </div>
        </section>

        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
          {/* The Pi: health.py's report and the actions on the machine. */}
          <section className="flex flex-col gap-3.5 rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-gray-600">
                <Cpu className="h-[18px] w-[18px]" />
                <h3 className="text-[15px] font-semibold text-gray-900">Pi</h3>
                <span className="ml-auto"><StatusPill status={healthStatus} /></span>
              </div>
              {healthFresh ? (
                <p className="text-[13px] text-gray-500">
                  {[
                    boardModel,
                    formatUptime(metrics?.uptimeSeconds) && `up ${formatUptime(metrics?.uptimeSeconds)}`,
                    `last report ${getTimeAgo(lastHealthReport)}`,
                  ].filter(Boolean).join(' · ')}
                </p>
              ) : (
                <p className="text-[13px] leading-relaxed text-gray-700">
                  {healthStatus === 'Pending'
                    ? 'This Pi has not reported yet.'
                    : `${boardModel ? `${boardModel} · ` : ''}last report ${formatWhen(lastHealthReport) || 'unknown'}. ` +
                      'Its readings are hidden while it is silent, because old figures send people after the wrong fault.'}
                </p>
              )}
            </div>

            {healthFresh && <Reasons reasons={healthReasons} />}

            {healthFresh && metrics && (
              <div className="grid grid-cols-3 gap-3 rounded-md bg-gray-50 p-3">
                {metrics.temperature != null && <Fact label="Temperature" value={`${metrics.temperature.toFixed(1)}°C`} />}
                {metrics.cpuUsage != null && <Fact label="CPU" value={`${Math.round(metrics.cpuUsage)}%`} />}
                {metrics.memoryUsage != null && <Fact label="Memory" value={`${Math.round(metrics.memoryUsage)}%`} />}
                {metrics.diskUsage != null && <Fact label="Disk" value={`${Math.round(metrics.diskUsage)}%`} />}
                {metrics.signalDbm != null && <Fact label="WiFi" value={`${metrics.signalDbm} dBm`} />}
                {status?.monitorConnected != null && (
                  <Fact
                    label="Monitor"
                    value={status.monitorConnected ? (status.monitorConnector || 'Connected') : 'Not connected'}
                  />
                )}
                {cameraDetected != null && (
                  <Fact
                    label="Camera"
                    value={!cameraDetected ? 'None'
                      : cameraType === 'usb' ? 'USB'
                      : cameraType === 'csi' ? 'Ribbon'
                      : 'Detected'}
                  />
                )}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <ActionButton
                icon={Power}
                label="Shut Down"
                hint="Stays off until someone at the site turns it back on"
                onClick={shutDown}
                disabled={!canSend}
                caution
              />
              <ActionButton
                icon={RotateCcw}
                label="Reboot"
                hint="Back in about a minute"
                onClick={reboot}
                disabled={!canSend}
              />
              {/* Pinage staff only. A shell on the device is not a customer-facing
                  feature at any level, so it is absent for them rather than
                  present and refusing. */}
              {isSuperAdmin && (
                <ActionButton
                  icon={Terminal}
                  label="Terminal"
                  hint={showTerminal ? 'Open below' : 'Command line on the Pi'}
                  onClick={() => setShowTerminal(open => !open)}
                  disabled={!reachable}
                />
              )}
            </div>
          </section>

          {/* Pinage: system.py's report and the actions on the signage services. */}
          <section className="flex flex-col gap-3.5 rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-gray-600">
                <MonitorPlay className="h-[18px] w-[18px]" />
                <h3 className="text-[15px] font-semibold text-gray-900">Pinage</h3>
                <span className="ml-auto"><StatusPill status={pinageStatus} /></span>
              </div>
              {pinageFresh && (
                <p className="text-[13px] text-gray-500">
                  {status?.currentPlaylist
                    ? `Playing “${status.currentPlaylist}” · last report ${getTimeAgo(lastPinageReport)}`
                    : `Last report ${getTimeAgo(lastPinageReport)}`}
                </p>
              )}
              {pinageStopped && (
                <p className="text-[13px] leading-relaxed text-gray-700">
                  Pinage last reported {getTimeAgo(lastPinageReport)} while the Pi is reporting
                  normally, so it has been stopped or has crashed.
                </p>
              )}
              {pinageStatus === 'Unknown' && (
                <p className="text-[13px] leading-relaxed text-gray-700">
                  Nothing from the Pi either, so there is no telling what Pinage is doing.
                </p>
              )}
              {pinageStatus === 'Pending' && (
                <p className="text-[13px] leading-relaxed text-gray-700">Pinage has not reported yet.</p>
              )}
            </div>

            {pinageFresh && <Reasons reasons={pinageReasons} />}

            {pinageFresh && Object.keys(services).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(services).map(([name, state]) => {
                  const waitingForCamera = name === 'audience' && noCamera;
                  const dot = waitingForCamera ? 'bg-gray-400'
                    : state === 'running' ? 'bg-green-600'
                    : state === 'stale' || state === 'starting' ? 'bg-amber-500'
                    : state === 'unknown' ? 'bg-gray-400'
                    : 'bg-red-600';
                  const label = name.charAt(0).toUpperCase() + name.slice(1);
                  return (
                    <span
                      key={name}
                      title={state}
                      className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-700"
                    >
                      <span className={`h-2 w-2 rounded-full ${dot}`} />
                      {waitingForCamera ? `${label} · waiting for camera` : label}
                    </span>
                  );
                })}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <ActionButton
                icon={Play}
                label="Start"
                hint={pinageStopped ? 'Brings the screen back' : "Starts anything that isn't running"}
                onClick={() => execute('start-player')}
                disabled={!canSend}
                primary={pinageStopped}
              />
              <ActionButton
                icon={Square}
                label="Stop"
                hint={pinageStopped ? 'Already stopped' : 'Screen stays dark until started again'}
                onClick={stop}
                disabled={!canSend || pinageStopped}
                caution={!pinageStopped}
              />
              <ActionButton
                icon={RotateCw}
                label="Restart"
                hint="Screen blanks for a few seconds"
                onClick={() => execute('restart-player')}
                disabled={!canSend}
              />
            </div>

            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              <span>Software</span>
              <span className="h-px flex-grow bg-gray-200" />
            </div>

            <div className="grid grid-cols-2 gap-3 rounded-md bg-gray-50 p-3">
              <Fact
                label="Installed"
                value={[
                  pinageVersion ? `v${pinageVersion}` : 'Version not reported',
                  pinageBuild && `build ${pinageBuild}`,
                ].filter(Boolean).join(' · ')}
              />
              {stagedBuild && stagedBuild !== pinageBuild && (
                <Fact label="Downloaded, not installed" value={`build ${stagedBuild}`} />
              )}
              {/* A customer cannot act on this, so it would only prompt the
                  question of why their device is behind. */}
              {isSuperAdmin && (
                <Fact
                  label="Latest published"
                  value={currentBuild?.available
                    ? (currentBuild.build ? `build ${currentBuild.build}` : 'No build id recorded')
                    : 'Unknown'}
                />
              )}
            </div>

            {/* Update and Roll Back are Pinage staff only, matching the API: the
                Pis belong to the hosting company and the content belongs to the
                customers, and moving a device between releases is work on the
                estate rather than on anybody's content. */}
            {isSuperAdmin && (
              <div className="flex flex-col gap-2">
                <ActionButton
                  icon={ArrowUpCircle}
                  label="Update"
                  hint={updateHint}
                  onClick={update}
                  disabled={!canSend}
                />
                <ActionButton
                  icon={Undo2}
                  label="Roll Back"
                  hint="Back to the release before the last update"
                  onClick={rollBack}
                  disabled={!canSend}
                />
              </div>
            )}
          </section>
        </div>

        {showTerminal && isSuperAdmin && (
          <DeviceTerminal
            deviceId={deviceId}
            deviceName={deviceName}
            onClose={() => setShowTerminal(false)}
          />
        )}

        {/* What has been sent to this device, by whom, and how it went. Also
            where a command queued for a device that went away can be
            cancelled before it runs days later. */}
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-gray-600">
            <History className="h-[18px] w-[18px]" />
            <h3 className="text-[15px] font-semibold text-gray-900">Recent commands</h3>
          </div>

          {rows.length === 0 ? (
            <p className="text-sm text-gray-500">Nothing has been sent to this device in the last week.</p>
          ) : (
            <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
              {rows.map(command => {
                const outcome = describeOutcome(command);
                const live = inFlight?.id === command.id;
                const output = live
                  ? (command.status === 'pending'
                    ? 'Waiting for the device to pick this up...'
                    : command.result || 'Running...')
                  : CAPTURE_COMMANDS.has(command.command) && outcome.label === 'Completed'
                    ? null
                    : command.result;
                const open = expanded === command.id && !!output;

                return (
                  <li key={command.id}>
                    <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)_minmax(0,1.3fr)_minmax(0,1.6fr)] items-center gap-3 px-4 py-2 text-[13px]">
                      <button
                        type="button"
                        onClick={() => setExpanded(open ? null : command.id)}
                        disabled={!output}
                        aria-expanded={open}
                        className="text-left font-medium text-gray-900 hover:underline disabled:cursor-default disabled:no-underline"
                      >
                        {COMMAND_NAMES[command.command] ?? command.command}
                      </button>
                      <span className="truncate text-gray-600" title={command.issuedBy}>
                        {describeIssuer(command.issuedBy)}
                      </span>
                      <span className="text-gray-600">
                        {[formatWhen(command.issuedAt), describeDuration(command)].filter(Boolean).join(' · ')}
                      </span>
                      <span className="flex items-center justify-end gap-2.5">
                        <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${outcome.className}`}>
                          {outcome.label}
                        </span>
                        {command.status === 'pending' && (
                          <button
                            type="button"
                            onClick={() => cancel(command.id)}
                            className="h-9 rounded-md border border-gray-300 bg-white px-3 text-[13px] font-medium text-gray-800 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        )}
                      </span>
                    </div>
                    {open && (
                      <pre className="mx-4 mb-3 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md bg-gray-50 p-3 font-mono text-xs text-gray-800">
                        {output}
                      </pre>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};
